import type { Pool } from "../hooks/usePools";
import type { RedKeel } from "../config/networks";
import { esEstable } from "../lib/precio";
import { ParMonedas, IconoRed } from "./Monedas";

/**
 * La ficha de datos REALES de la pool elegida: una cabecera que acompaña los pasos de "abrir una
 * piscina" para no perder de vista EN QUÉ estás invirtiendo mientras configuras el rango y la
 * cantidad. Todo sale del índice del VPS (el mismo `pools*.json` que llena el selector):
 *
 *   · Red, par y comisión               — de la propia pool
 *   · Profundidad (TVL) y ganancia/año   — el indexer los mide on-chain (feeGrowth / TVL)
 *   · Volumen 24h y comisiones 24h       — el indexer solo publica el ANUAL; aquí se divide entre
 *                                          365 (misma foto, a escala de un día). Comisiones 24h =
 *                                          TVL · APR/365; Volumen 24h = volumen anual / 365.
 *
 * Si el indexer aún no tiene ventana (pool recién descubierta, o fallback on-chain sin métricas) las
 * cifras salen a 0 → se pinta «—», nunca un cero que parezca una pool muerta.
 */
function usd(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n >= 1e9) return "$" + (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return "$" + (n / 1e3).toFixed(1) + "K";
  return "$" + n.toLocaleString("es", { maximumFractionDigits: 1 });
}

export function FichaPool({ pool, red }: { pool: Pool; red: RedKeel }) {
  // El activo (lo que NO es el estable) va primero, igual que en el selector: RBLX / USDG.
  const estable0 = esEstable(pool.token0.symbol) && !esEstable(pool.token1.symbol);
  const activo = estable0 ? pool.token1.symbol : pool.token0.symbol;
  const estable = estable0 ? pool.token0.symbol : pool.token1.symbol;

  const apr = pool.aprPct ?? 0;
  const tvl = pool.tvlUsd ?? 0;
  const vol24 = (pool.volumeYearUsd ?? 0) / 365;
  const fees24 = (tvl * apr) / 100 / 365;

  const marca = red.etiqueta.split(" · ")[0]; // «Robinhood», «Uniswap»…

  return (
    <div className="ficha-pool">
      <div className="fp-top">
        <ParMonedas s0={activo} s1={estable} size={30} />
        <div className="fp-id">
          <span className="fp-par">{activo} <em>/ {estable}</em></span>
          <span className="fp-sub">Uniswap V3 · {pool.fee / 10000}%</span>
        </div>
        <div className="fp-apr">
          <span className="v">{apr ? `${apr.toFixed(1)}%` : "—"}</span>
          <span className="k">ganancia/año</span>
        </div>
      </div>
      <dl className="fp-datos">
        <div>
          <dt>Red</dt>
          <dd className="fp-red"><IconoRed clave={red.clave} size={15} />{marca}</dd>
        </div>
        <div>
          <dt>Profundidad</dt>
          <dd>{usd(tvl)}</dd>
        </div>
        <div>
          <dt>Volumen 24h</dt>
          <dd>{usd(vol24)}</dd>
        </div>
        <div>
          <dt>Comisiones 24h</dt>
          <dd className="fp-fees">{usd(fees24)}</dd>
        </div>
      </dl>
    </div>
  );
}
